<template>
  <Dialog
    title="Upload Design Samples"
    :show-close-button="true"
    @close-event="handleClose"
    :show-footer="true"
    :custom-footer="true"
    md
  >
    <template #modal-content>
      <div>
        <!--
          AB#21611: the server silently discards uploads past its limit, so say the ceiling
          up front rather than after a multi-megabyte upload has already been sent.
        -->
        <p
          v-if="maxUploads"
          class="px-3 pt-3 text-sm"
          :class="overLimit ? 'text-red-600 font-semibold' : 'text-surface-6'"
        >
          <template v-if="overLimit">
            {{ uploadCount }} of {{ maxUploads }} files attached — too many to submit at once.
            Please submit about {{ Math.floor(maxUploads / 2) }} products at a time.
          </template>
          <template v-else>
            {{ uploadCount }} of {{ maxUploads }} files attached (each product uses 2 — front and
            back).
          </template>
        </p>
        <div
          v-if="
            design_approval_activity_item_data[design_approval_activity_navigation_index] &&
            modalType === 'test_sample'
          "
          class="flex justify-between gap-2 py-4 px-3 mx-auto"
        >
          <div
            class="w-1/2"
            v-for="(actFile, fileInd) in design_approval_activity_item_data[
              design_approval_activity_navigation_index
            ].files"
            :key="`actfile-${fileInd}`"
          >
            <template
              v-if="
                design_approval_activity_item_data[design_approval_activity_navigation_index]
                  .product_files[fileInd]
              "
            >
              <div class="text-xl font-bold uppercase">{{ actFile.side }}</div>

              <label class="relative block">
                <button
                  v-if="actFile.file"
                  @click="deleteFile(design_approval_activity_navigation_index, fileInd)"
                  class="absolute top-0 right-0 p-1 text-red-500 hover:text-red-700"
                >
                  <XCircleIcon class="size-7" />
                </button>

                <template v-if="actFile.file">
                  <div class="rounded border border-line overflow-hidden">
                    <img
                      :src="setSampleImage(design_approval_activity_navigation_index, fileInd)"
                      class="w-full max-w-[350px]"
                    />
                  </div>
                </template>

                <input
                  v-if="!actFile.file"
                  type="file"
                  @change.stop="
                    changeSampleFile(
                      ($event.target as HTMLInputElement).files,
                      design_approval_activity_navigation_index,
                      fileInd
                    )
                  "
                  class="mt-1 z-10"
                />
              </label>

              <div
                v-if="
                  design_approval_activity_item_data[design_approval_activity_navigation_index]
                    .rejected_files.length === 2 && !actFile.file
                "
                class="mt-2"
              >
                <img
                  :src="`${storage_url}/${design_approval_activity_item_data[design_approval_activity_navigation_index].rejected_files[fileInd]}`"
                  class="w-full max-w-[350px] border border-line p-2 rounded"
                  v-img-fallback
                />
                <span
                  v-if="
                    design_approval_activity_item_data[design_approval_activity_navigation_index]
                      .rejected_msg && fileInd === 0
                  "
                  class="block text-sm mt-1"
                >
                <b>Feedback:</b>  {{
                    design_approval_activity_item_data[design_approval_activity_navigation_index]
                      .rejected_msg
                  }}
                </span>
              </div>

              <div class="mt-1">
                <img
                  :src="`${storage_url}/${design_approval_activity_item_data[design_approval_activity_navigation_index].product_files[fileInd]}`"
                  class="rounded border"
                  v-img-fallback
                />
              </div>
            </template>
          </div>
        </div>

        <!-- Skip Reason Message Box -->
        <div
          v-if="
            design_approval_activity_item_data[design_approval_activity_navigation_index]
              ?.skip_customer_approval.show_reason_modal
          "
          class="fixed top-0 left-0 w-full h-full z-[9999] flex items-center justify-center bg-black bg-opacity-20"
          :key="`design-approval-${design_approval_activity_navigation_index}`"
        >
          <div class="bg-surface p-6 rounded-lg shadow-xl w-full max-w-lg">
            <textarea
              rows="4"
              placeholder="Enter reason to skip"
              class="w-full border rounded p-2"
              v-model="
                design_approval_activity_item_data[design_approval_activity_navigation_index]
                  .skip_customer_approval.reason
              "
            ></textarea>
            <div class="flex justify-end gap-2 mt-4">
              <button
                class="px-4 py-2 text-ink-soft"
                @click="
                  closeMessage(
                    design_approval_activity_item_data,
                    design_approval_activity_navigation_index
                  )
                "
              >
                Cancel
              </button>
              <button
                class="px-4 py-2 bg-blue-600 text-white rounded"
                @click="
                  updateReason(
                    design_approval_activity_item_data,
                    design_approval_activity_navigation_index
                  )
                "
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
    <!-- Footer Slot -->
    <template #footer>
      <div class="flex justify-end flex-wrap gap-2">
        <button class="px-4 py-2 bg-surface-3 text-ink rounded" @click="handleCancel">
          Cancel
        </button>

        <template v-if="design_approval_activity_item_data.length">
          <button
            class="px-4 py-2 bg-surface-3 text-ink rounded"
            @click="
              skipSampleApproval(
                design_approval_activity_item_data,
                design_approval_activity_navigation_index
              )
            "
          >
            Skip Sample Approval
          </button>
        </template>

        <template v-if="design_approval_activity_navigation_index > 0">
          <button
            class="px-4 py-2 bg-surface-3 text-white rounded"
            @click="navigateSampleFiles('back')"
          >
            Previous
          </button>
        </template>

        <template
          v-if="
            design_approval_activity_item_data.length - 1 ===
            design_approval_activity_navigation_index
          "
        >
          <button
            class="px-4 py-2 bg-primary text-white rounded disabled:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed"
            @click="submitSampleFiles"
            :disabled="inValid"
          >
            Submit Samples
          </button>
        </template>

        <template v-else-if="design_approval_activity_item_data.length">
          <button
            class="px-4 py-2 bg-surface-3 text-white rounded"
            @click="navigateSampleFiles('next')"
          >
            Next
          </button>
        </template>
      </div>
    </template>
  </Dialog>
</template>
<script setup lang="ts">
import Dialog from '@/components/Shared/Dialog.vue';
import { storage_url } from '@/composables/index';
import { DesignApprovalActivityItem } from '@/models';
import { TrashIcon, XCircleIcon, XMarkIcon } from '@heroicons/vue/24/solid';
import { computed, ref } from 'vue';
interface Props {
  design_approval_activity_item_data: DesignApprovalActivityItem[];
  modalType: string;
  design_approval_activity_navigation_index: number;
  activity_items: any;
  inValid: boolean;
  /** AB#21611: files queued so far, and the per-submission ceiling. */
  uploadCount?: number;
  maxUploads?: number;
}
const props = defineProps<Props>();

/** AB#21611: drives the counter's warning state; submission itself is blocked in the parent. */
const overLimit = computed(
  () => Boolean(props.maxUploads) && (props.uploadCount ?? 0) > (props.maxUploads ?? 0)
);
// const design_approval_activity_navigation_index = ref<number>(0);

const emits = defineEmits([
  'close',
  'cancel',
  'skip-sample-approval',
  'navigate-sample-files',
  'submit-sample-files',
  'change-sample-file',
  'delete-file',
  'close-message',
  'update-reason',
]);

const handleClose = () => {
  emits('close');
};
const handleCancel = () => {
  emits('cancel');
};
const skipSampleApproval = (data, index) => {
  emits('skip-sample-approval', data, index);
};
const navigateSampleFiles = (direction) => {
  emits('navigate-sample-files', direction);
};
const submitSampleFiles = () => {
  emits('submit-sample-files');
};
const changeSampleFile = (files, navIndex, fileInd) => {
  emits('change-sample-file', files, navIndex, fileInd);
};
const deleteFile = (navIndex, fileInd) => {
  emits('delete-file', navIndex, fileInd);
};
const closeMessage = (data, navIndex) => {
  emits('close-message', data, navIndex);
};
const updateReason = (data, navIndex) => {
  emits('update-reason', data, navIndex);
};
const setSampleImage = (act_item_index, file_index) => {
  if (props.modalType === 'test_sample') {
    return URL.createObjectURL(
      props.design_approval_activity_item_data[act_item_index].files[file_index].file
    );
  } else {
    return URL.createObjectURL(
      props.activity_items.activity_item_data[act_item_index].files[file_index].file
    );
  }
};
</script>
<style lang="scss" scoped></style>
